import { useCallback, useEffect, type ReactNode } from 'react';
import { NavLink, Route, Routes, useLocation, useNavigate } from 'react-router';
import type { TelegramApi } from '../telegram/webapp';
import s from './shell.module.css';

const TABS = [
  { to: '/', label: 'Главная', icon: <><path d="M4 11l8-7 8 7v9H4z" /><path d="M10 20v-5h4v5" /></> },
  { to: '/passes', label: 'Пропуска', icon: <><rect x="4" y="5" width="16" height="14" rx="2" /><path d="M8 10h8M8 14h5" /></> },
  { to: '/requests', label: 'Обращения', icon: <path d="M5 5h14v10H10l-5 4z" /> },
  { to: '/more', label: 'Ещё', icon: <><circle cx="6" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="18" cy="12" r="1.4" /></> },
];

/** Screens of the next steps; each says honestly that it is not ready yet. */
function Upcoming({ title }: { title: string }) {
  return (
    <main className={s.upcoming}>
      <h1 className={s.upcomingTitle}>{title}</h1>
      <p className={s.upcomingText}>Этот раздел появится в одном из ближайших обновлений.</p>
    </main>
  );
}

/** Nested screen: no tab bar, the messenger's native back button returns (spec §6.1); a browser gets one on the page. */
function Nested({ telegram, children }: { telegram: TelegramApi; children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const back = useCallback(
    () => (location.key === 'default' ? navigate('/', { replace: true }) : navigate(-1)),
    [navigate, location.key],
  );
  useEffect(() => telegram.showBackButton(back), [telegram, back]);
  if (telegram.messenger) return <>{children}</>;
  return (
    <>
      <button type="button" className={s.back} onClick={back}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 5l-7 7 7 7" />
        </svg>
        Назад
      </button>
      {children}
    </>
  );
}

function Tabs({ children }: { children: ReactNode }) {
  return (
    <div className={s.withTabs}>
      <div className={s.content}>{children}</div>
      <nav aria-label="Разделы" className={s.tabs}>
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end className={({ isActive }) => (isActive ? `${s.tab} ${s.tabActive}` : s.tab)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              {t.icon}
            </svg>
            <span>{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

interface ShellProps {
  telegram: TelegramApi;
  home: ReactNode;
  newPass?: ReactNode;
  invite?: ReactNode;
  passes?: ReactNode;
  passCard?: ReactNode;
  requests?: ReactNode;
  newRequest?: ReactNode;
  requestChat?: ReactNode;
  more?: ReactNode;
  /** Nested screens without the tab bar: path → screen. */
  nested?: Record<string, ReactNode>;
}

export function Shell({ telegram, home, newPass, invite, passes, passCard, requests, newRequest, requestChat, more, nested: extra = {} }: ShellProps) {
  const nested = (title: string) => (
    <Nested telegram={telegram}>
      <Upcoming title={title} />
    </Nested>
  );
  return (
    <Routes>
      <Route path="/" element={<Tabs>{home}</Tabs>} />
      <Route path="/passes" element={<Tabs>{passes ?? <Upcoming title="Пропуска" />}</Tabs>} />
      <Route path="/requests" element={<Tabs>{requests ?? <Upcoming title="Обращения" />}</Tabs>} />
      {newRequest && <Route path="/requests/new" element={<Nested telegram={telegram}>{newRequest}</Nested>} />}
      {requestChat && <Route path="/requests/:id" element={<Nested telegram={telegram}>{requestChat}</Nested>} />}
      <Route path="/more" element={<Tabs>{more ?? <Upcoming title="Ещё" />}</Tabs>} />
      {Object.entries(extra).map(([path, element]) => (
        <Route key={path} path={path} element={<Nested telegram={telegram}>{element}</Nested>} />
      ))}
      <Route path="/new" element={newPass ? <Nested telegram={telegram}>{newPass}</Nested> : nested('Заказ пропуска')} />
      <Route path="/invite" element={invite ? <Nested telegram={telegram}>{invite}</Nested> : nested('Ссылка для гостя')} />
      <Route path="/passes/:id" element={passCard ? <Nested telegram={telegram}>{passCard}</Nested> : nested('Карточка пропуска')} />
      {!extra['/notifications'] && <Route path="/notifications" element={nested('Уведомления')} />}
      <Route path="*" element={<Tabs>{home}</Tabs>} />
    </Routes>
  );
}
